import { Link } from '@mui/material';

import {
  Column,
  LinkBehaviour,
  TableHeader,
  Typography,
} from '@cast/design-system';
import { Link as RouterLink } from 'components/router';
import { sumDataTransferCost, sumDataTransferTraffic } from '@cast/utils';

import { ExportFileButton } from 'components/buttons';
import { Money } from 'components/money';
import { getRowRankClass } from 'components/table';
import { NetworkCostTable } from 'features/cost-report/components/network-cost/NetworkCostTable';
import { TrafficCell } from 'features/cost-report/components/network-cost/TrafficCell';
import { WorkloadDetailsLinkCell } from 'features/cost-report/components/WorkloadDetailsLinkCell';
import { getNetworkCostPrecision } from 'features/cost-report/utils/networkCosts';

import { useReportDownload } from './_hooks/useReportDownload';
import { NamespacesLinkCell } from '../../../../../cost-report/components/NamespacesLinkCell';
import { WorkloadDataTransfer } from '../_types';
import { useWorkloadsNetworkCostOverTime } from '../providers';

import {
  NoData,
  NoResults,
  SearchableTableRecordsCount,
  useSearchContext,
} from '@cast/shared-search-ui';

type WorkloadsListSectionProps = {
  cacheKey: string;
  disableWorkloadLink?: boolean;
  getWorkloadLink?: (workload: WorkloadDataTransfer) => string | undefined;
};

export const WorkloadsListSection = ({
  cacheKey,
  disableWorkloadLink,
  getWorkloadLink,
}: WorkloadsListSectionProps) => {
  const ctx = useSearchContext<WorkloadDataTransfer>();
  const { topMostExpensiveWorkloads } = useWorkloadsNetworkCostOverTime();
  const { download } = useReportDownload(
    'workload-network-spend-details',
    ctx?.data || []
  );

  return (
    <NetworkCostTable
      cacheKey={cacheKey}
      testId="workload-network-details-table"
      rowKey={(row: WorkloadDataTransfer) =>
        `${row.namespace}-${row.workloadName}-${row.workloadType}`
      }
      getRowClass={(row) =>
        getRowRankClass(
          topMostExpensiveWorkloads.findIndex(({ id }) => id === row.id)
        )
      }
      components={{
        noData: <NoData entity="workload" />,
        noResults: <NoResults entity="workload" />,
      }}
      outerHeader={
        <TableHeader
          recordsCount={
            <SearchableTableRecordsCount
              title="workload"
              outOf={ctx?.metadata}
            />
          }
          actions={
            <ExportFileButton onClick={download} testId="download-csv-report" />
          }
          showSettings={false}
        />
      }
      showDetails
    >
      <Column<WorkloadDataTransfer>
        id="workloadName"
        minWidth={320}
        header="Workload"
        renderCell={(workload) => {
          const { workloadName, workloadType, namespace } = workload;

          if (disableWorkloadLink) {
            return (
              <Typography variant="inherit" fontWeight={500} noWrap>
                {workloadName}
              </Typography>
            );
          }

          const inventoryLink = getWorkloadLink?.(workload);

          if (inventoryLink) {
            return (
              <Link to={inventoryLink} component={RouterLink} underline="none">
                <LinkBehaviour variant="inherit" fontWeight={500} noWrap>
                  {workloadName}
                </LinkBehaviour>
              </Link>
            );
          }

          return (
            <WorkloadDetailsLinkCell
              workload={workloadName}
              namespace={namespace}
              workloadType={workloadType}
            />
          );
        }}
        resizable
        sortable
        accessor="workloadName"
      />

      <Column id="workloadType" width={116} header="Type" sortable />

      <Column<WorkloadDataTransfer>
        id="namespace"
        minWidth={144}
        header="Namespace"
        renderCell={({ namespace }) => {
          return <NamespacesLinkCell namespace={namespace} />;
        }}
        sortable
      />

      <Column<WorkloadDataTransfer>
        id="totalBytes"
        width={232}
        header="Total Traffic"
        renderCell={(row) => {
          const egress = sumDataTransferTraffic(row.egress);
          const ingress = sumDataTransferTraffic(row.ingress);
          return <TrafficCell egress={egress} ingress={ingress} />;
        }}
        sortable
        accessor={(row) => {
          const egress = sumDataTransferTraffic(row.egress);
          const ingress = sumDataTransferTraffic(row.ingress);
          return egress + ingress;
        }}
      />

      <Column<WorkloadDataTransfer>
        id="totalCost"
        width={228}
        header="Total Cost"
        infoTooltip="Sum of egress and ingress traffic cost. Ingress does not incur any costs, depending on the cloud provider"
        bodyCellSx={{ fontWeight: 'bold' }}
        renderCell={(row) => {
          const egress = sumDataTransferCost(row.egress);
          const ingress = sumDataTransferCost(row.ingress);
          const total = egress + ingress;
          const precision = getNetworkCostPrecision(total);
          return (
            <Money amount={total} precision={precision} fixed={precision} />
          );
        }}
        sortable
        accessor={(row) => {
          const egress = sumDataTransferCost(row.egress);
          const ingress = sumDataTransferCost(row.ingress);
          return egress + ingress;
        }}
        defaultSort="desc"
      />
    </NetworkCostTable>
  );
};
